import React, { useState } from 'react';
import styled from 'styled-components';
import { Shield, Mail, HelpCircle, MessageCircle } from 'lucide-react';
import LoginLeftSide from '../components/LoginLeftSide';

// -------------------- Styled Components --------------------

// Outer page container with a gradient background
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column; /* Stack content by default for mobile */
  background: linear-gradient(180deg, #1a181c, #533d66);

  @media (min-width: 1024px) {
    flex-direction: row; /* Side-by-side on larger screens */
  }
`;

// Right section (only section used in this page)
const RightSection = styled.div`
  width: 100%;
  background: white;
  padding: 2rem;
  box-sizing: border-box; /* Ensure padding doesn't overflow */

  @media (min-width: 1024px) {
    width: 50%; /* Take half the width on larger screens */
  }
`;

// Center the content within the right section
const RightContent = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column; /* Stack elements vertically */
  width: 100%;
  box-sizing: border-box; /* Prevent overflow */
`;

// Container for the form and related elements
const FormContainer = styled.div`
  width: 100%;
  max-width: 28rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  box-sizing: border-box;
`;

// Header for the form (title and subtitle)
const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  width: 100%;
`;

// Form title
const FormTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.5rem; /* Adjust font size for small screens */
  }
`;

// Form subtitle
const FormSubtitle = styled.p`
  font-size: 1rem;
  color: #4b5563;

  @media (max-width: 768px) {
    font-size: 0.875rem; /* Smaller font size on mobile */
  }
`;

// Form element styling
const FormElement = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// Group for label and input
const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Styled label
const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

// Wrapper for the input field and its icon
const InputWrapper = styled.div`
  position: relative;
`;

// Container for the input icon
const InputIcon = styled.div`
  position: absolute;
  inset-y: 0;
  left: 0.75rem;
  top: 0.567rem;
  display: flex;
  align-items: center;
  pointer-events: none;
  color: #9ca3af;
`;

// Styled input field
const InputField = styled.input`
  display: block;
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  color: #1f2937;
  background: white;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    border-color: transparent;
    box-shadow: 0 0 0 2px #533d66;
  }
`;

// Submit button styling
const SubmitButton = styled.button`
  width: 100%;
  background: #533d66;
  color: white;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #422d52;
  }
`;

// Text below the form for account creation prompt
const AccountText = styled.p`
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;

  @media (max-width: 768px) {
    font-size: 0.75rem; /* Smaller font size on mobile */
  }
`;

// Styled link for creating an account
const AccountLink = styled.a`
  font-weight: 600;
  color: #533d66;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #422d52;
  }
`;

// Container for support links (Help & Contact Support)
const SupportLinksContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  gap: 2rem;
  color: #4b5563;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem; /* Stack support links vertically on mobile */
  }
`;

// Individual support link styling
const SupportLinks = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6b7280;

  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: #6b7280;
    transition: color 0.3s;

    &:hover {
      color: #374151;
    }

    svg {
      margin-right: 0.25rem;
    }
  }
`;

// Container for the icon inside support links
const SupportIconContainer = styled.div`
  padding: 0.5rem;
  border-radius: 0.5rem;
  background: #f3f4f6;
  transition: background 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

//
// Continue With OTP Page Component
//
const ContinueWithOtpPage = () => {
    const [email, setEmail] = useState('');
    const [isOtpSent, setIsOtpSent] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsOtpSent(true);
        // Handle OTP sending logic here
    };

    return (
        <PageContainer>
            <LoginLeftSide />
            <RightSection>
                <RightContent>
                    <FormContainer>
                        <FormHeader>
                            <FormTitle>Welcome Back</FormTitle>
                            <FormSubtitle>Sign in to your account to continue</FormSubtitle>
                        </FormHeader>
                        <FormElement onSubmit={handleSubmit}>
                            <InputGroup>
                                <Label htmlFor="email">Email Address</Label>
                                <InputWrapper>
                                    <InputIcon>
                                        <Mail size={20} />
                                    </InputIcon>
                                    <InputField
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="Enter your email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </InputWrapper>
                            </InputGroup>
                            <SubmitButton type="submit">
                                Continue with OTP
                            </SubmitButton>
                        </FormElement>
                        <AccountText>
                            Don't have an account?{' '}
                            <AccountLink href="https://vapt.zerodayops.com/signup">
                                Create Account
                            </AccountLink>
                        </AccountText>
                        <SupportLinksContainer>
                            <SupportLinks>
                                <a href="mailto:support@zerodayops.com">
                                    <HelpCircle size={16} /> Help
                                </a>
                                <a href="mailto:contact@zerodayops.com">
                                    <MessageCircle size={16} /> Contact Support
                                </a>
                            </SupportLinks>
                        </SupportLinksContainer>
                    </FormContainer>
                </RightContent>
            </RightSection>
        </PageContainer>
    );
};

export default ContinueWithOtpPage;
